/**
 * useCanonicalFunnelGraph - Shared hook for loading and managing canonical funnel graphs (MA-FE-073)
 * 
 * This hook provides:
 * 1. Load the latest funnel from canonical backend state
 * 2. Normalize/return only the canonical graph from `draft.content`
 * 3. Validate presence of nodes, edges, entry_node_id
 * 4. Derive deterministic step/review order from the graph
 * 5. Expose a single source of truth for graph, loading, error state, review order
 * 
 * Used by both AI Review and Funnel Builder to ensure consistent behavior.
 */

import { useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { funnelKeys } from "@/hooks/useFunnels";

/**
 * Graph validation result
 */
function validateGraph(graph) {
  const errors = [];
  const warnings = [];
  
  if (!graph) {
    errors.push("Graph is null or undefined");
    return { isValid: false, errors, warnings };
  }
  
  // Check nodes
  if (!graph.nodes || !Array.isArray(graph.nodes)) {
    errors.push("Graph nodes missing or invalid");
  } else if (graph.nodes.length === 0) {
    errors.push("Graph has no nodes");
  }
  
  // Check entry_node_id
  if (!graph.entry_node_id) {
    errors.push("Graph missing entry_node_id");
  } else {
    const nodeIds = new Set((graph.nodes || []).map(n => n.id));
    if (!nodeIds.has(graph.entry_node_id)) {
      errors.push(`Entry node '${graph.entry_node_id}' not found in nodes`);
    }
  }
  
  // Check edges (warning only for draft mode)
  if (!graph.edges || !Array.isArray(graph.edges)) {
    warnings.push("Graph edges missing - using empty array");
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Derive deterministic review order from graph traversal
 * 
 * Starting from entry_node_id, follows edges to determine order.
 * For branches, uses depth-first traversal with deterministic ordering.
 */
function deriveReviewOrder(graph) {
  if (!graph || !graph.nodes || !graph.entry_node_id) {
    return [];
  }
  
  const nodeMap = new Map(graph.nodes.map(n => [n.id, n]));
  const edges = graph.edges || [];
  
  // Build adjacency list (source -> [targets])
  const adjacency = new Map();
  for (const edge of edges) {
    const source = edge.source_node_id;
    const target = edge.target_node_id;
    if (source && target) {
      if (!adjacency.has(source)) {
        adjacency.set(source, []);
      }
      adjacency.get(source).push({ target, priority: edge.priority || 0 });
    }
  }
  
  // Sort adjacency targets by priority for deterministic order
  for (const [source, targets] of adjacency.entries()) {
    targets.sort((a, b) => a.priority - b.priority);
  }
  
  // Depth-first traversal
  const visited = new Set();
  const order = [];
  
  function visit(nodeId) {
    if (visited.has(nodeId) || !nodeMap.has(nodeId)) {
      return;
    }
    visited.add(nodeId);
    order.push(nodeMap.get(nodeId));
    
    const targets = adjacency.get(nodeId) || [];
    for (const { target } of targets) {
      visit(target);
    }
  }
  
  visit(graph.entry_node_id);
  
  // Add any orphan nodes not reached by traversal
  for (const node of graph.nodes) {
    if (!visited.has(node.id)) {
      order.push(node);
    }
  }
  
  return order;
}

/**
 * Get step number label for a node (e.g., "Step 1", "Step 2")
 */
function getStepNumber(nodeId, reviewOrder) {
  const index = reviewOrder.findIndex(n => n.id === nodeId);
  return index >= 0 ? index + 1 : null;
}

/**
 * Get node icon based on node_type
 */
function getNodeTypeIcon(nodeType) {
  const iconMap = {
    message: "MessageSquare",
    question: "HelpCircle",
    input: "TextCursorInput",
    consent: "Shield",
    booking: "Calendar",
    transfer: "PhoneForwarded",
    condition: "GitBranch",
  };
  return iconMap[nodeType] || "Circle";
}

/**
 * Get routing targets for a node from edges
 */
function getRoutingTargets(nodeId, graph) {
  if (!graph || !graph.edges) return [];
  
  const nodeMap = new Map((graph.nodes || []).map(n => [n.id, n]));
  
  return (graph.edges || [])
    .filter(e => e.source_node_id === nodeId)
    .sort((a, b) => (a.priority || 0) - (b.priority || 0))
    .map(edge => {
      const targetNode = nodeMap.get(edge.target_node_id);
      return {
        edgeId: edge.id,
        targetNodeId: edge.target_node_id,
        targetLabel: targetNode?.metadata?.label || targetNode?.node_type || edge.target_node_id,
        condition: edge.condition,
        priority: edge.priority || 0,
      };
    });
}

/**
 * Main hook for loading canonical funnel graph
 */
export function useCanonicalFunnelGraph(funnelId, options = {}) {
  const queryClient = useQueryClient();
  const { enabled = true } = options;
  
  // Fetch funnel data
  const {
    data: funnel,
    isLoading,
    error: fetchError,
    refetch,
  } = useQuery({
    queryKey: funnelKeys.detail(funnelId),
    queryFn: async () => {
      const { data } = await api.get(`/funnels/${funnelId}`);
      return data;
    },
    enabled: !!funnelId && enabled,
    staleTime: 0, // Always fetch fresh for review accuracy
  });
  
  // Extract canonical graph from draft.content
  const graph = useMemo(() => {
    if (!funnel) return null;
    // Canonical location is draft (which is actually draft.content from backend)
    return funnel.draft || null;
  }, [funnel]);
  
  // Validate graph
  const validation = useMemo(() => {
    return validateGraph(graph);
  }, [graph]);
  
  // Derive review order
  const reviewOrder = useMemo(() => {
    if (!validation.isValid) return [];
    return deriveReviewOrder(graph);
  }, [graph, validation.isValid]);
  
  // Combined error state
  const error = useMemo(() => {
    if (fetchError) {
      return {
        type: "fetch",
        message: fetchError.response?.data?.detail || "Failed to load funnel",
      };
    }
    if (!validation.isValid) {
      return {
        type: "validation",
        message: validation.errors[0] || "Invalid funnel graph",
        errors: validation.errors,
      };
    }
    return null;
  }, [fetchError, validation]);
  
  // Invalidate and refetch
  const invalidateAndRefetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: funnelKeys.detail(funnelId) });
    return refetch();
  }, [queryClient, funnelId, refetch]);
  
  // Get node by ID
  const getNode = useCallback((nodeId) => {
    if (!graph || !graph.nodes) return null;
    return graph.nodes.find(n => n.id === nodeId) || null;
  }, [graph]);
  
  // Get routing for a node
  const getRouting = useCallback((nodeId) => {
    return getRoutingTargets(nodeId, graph);
  }, [graph]);
  
  return {
    // Core data
    funnel,
    graph,
    reviewOrder,
    
    // State
    isLoading,
    error,
    isValid: validation.isValid,
    
    // Metadata
    isAIGenerated: funnel?.ai_generated || false,
    funnelName: funnel?.name || "",
    funnelSlug: funnel?.slug || "",
    draftVersion: funnel?.draft_version || 0,
    
    // Stats
    nodeCount: graph?.nodes?.length || 0,
    edgeCount: graph?.edges?.length || 0,
    entryNodeId: graph?.entry_node_id || null,
    
    // Actions
    refetch,
    invalidateAndRefetch,
    
    // Helpers
    getNode,
    getRouting,
    getStepNumber: (nodeId) => getStepNumber(nodeId, reviewOrder),
  };
}

// Export utility functions for external use
export { validateGraph, deriveReviewOrder, getNodeTypeIcon, getRoutingTargets };
